import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  TicketPlus,
  LogOut,
  Menu,
  UserCircle,
  Car,
  ShieldCheck,
  ClipboardCheck,
  Download,
  Smartphone,
  FolderKanban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { useMobile } from "@/hooks/use-mobile";
import { LogoAvatar } from "@/components/LogoUpload";
import { ROLE_LABELS } from "@/lib/constants";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { InstallApkDialog } from "@/components/InstallApkDialog";

export function Sidebar() {
  const [location] = useLocation();
  const { logout, profile } = useAuth();
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);
  const [installDialogOpen, setInstallDialogOpen] = useState(false);

  const roles = profile?.roles || [];
  const isAdmin = roles.includes("admin");
  const isOwner = roles.includes("owner");
  const isParceiro = roles.includes("parceiro_negocios");
  const isAuditor = roles.includes("auditor");
  const empresaTipo = profile?.empresa?.tipo ?? "";

  const navItems = [
    { label: "Painel", icon: LayoutDashboard, href: "/dashboard" },
    ...(!isParceiro && !isAuditor
      ? [{ label: "Empresas", icon: Building2, href: "/empresas" }]
      : []),
    ...(!isAuditor || isAdmin || isOwner
      ? [{ label: "Usuários", icon: Users, href: "/usuarios" }]
      : []),
    { label: "Dossiês", icon: FolderKanban, href: "/dossies" },
    { label: "Serviços técnicos", icon: ClipboardCheck, href: "/servicos" },
    { label: "Laudos", icon: FileText, href: "/laudos" },
    { label: "Veículos", icon: Car, href: "/veiculos" },
    ...(isAdmin || isOwner || roles.includes("rt")
      ? [{ label: "Convites", icon: TicketPlus, href: "/convites" }]
      : []),
    ...(isOwner || isAdmin
      ? [{ label: "Auditoria", icon: ShieldCheck, href: "/auditoria" }]
      : []),
    ...(isAuditor || isAdmin || isOwner
      ? [{ label: "Revisão Técnica", icon: ClipboardCheck, href: "/laudos/revisao" }]
      : []),
    ...(isOwner
      ? [
          {
            label: "Gerenciar APKs",
            icon: Smartphone,
            href: "/owner/apks",
          },
        ]
      : []),
  ];

  const displayRole =
    roles.length > 0
      ? roles
          .map((e) => {
            return ROLE_LABELS[e];
          })
          .join(", ")
      : "usuário";

  const NavContent = () => (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20 shrink-0">
            <span className="font-display font-bold text-xl text-white">R</span>
          </div>
          <div className="flex-1">
            <h1 className="font-display font-bold text-xl tracking-tight">
              ReMonta
            </h1>
            <p className="text-xs text-white/70">Serviços técnicos de sinistros</p>
          </div>
          <NotificationBell />
        </div>
      </div>

      <div className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link key={item.href} href={item.href} onClick={() => setOpen(false)}>
            <div
              data-testid={`nav-${item.href.replace("/", "")}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer",
                location === item.href || (item.href === "/servicos" && location.startsWith("/servicos/")) || (item.href === "/dossies" && location.startsWith("/dossies/"))
                  ? "bg-primary text-white shadow-md shadow-primary/25"
                  : "text-white/70 hover:text-white hover:bg-white/10",
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </div>
          </Link>
        ))}
      </div>

      <div className="p-4 border-t border-white/10">
        <div className="h-10">
          <Button
            size="sm"
            variant="outline"
            className="text-primary font-bold gap-2"
            data-testid="button-cta-download"
            onClick={() => setInstallDialogOpen(true)}
          >
            <Download className="w-5 h-5" />
            Baixar o App
          </Button>
        </div>
        <Link href="/perfil" onClick={() => setOpen(false)}>
          <div
            className="flex items-center gap-3 mb-4 px-2 cursor-pointer rounded-lg py-2 hover:bg-white/10 transition-colors"
            data-testid="nav-perfil"
          >
            {profile?.empresa ? (
              <LogoAvatar
                empresaId={profile.empresa.id}
                logoUrl={profile.empresa.logoUrl}
                fallback={profile?.nome?.substring(0, 2).toUpperCase() || "?"}
                size="xs"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white">
                {profile?.nome?.substring(0, 2).toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium truncate">
                {profile?.nome || "Carregando..."}
              </p>
              <p className="text-xs text-white/50 truncate uppercase">
                {displayRole}
              </p>
            </div>
            <UserCircle className="w-4 h-4 text-white/40" />
          </div>
        </Link>
        <Button
          variant="outline"
          className="w-full border-white/20 text-white hover:bg-white/10 hover:text-white hover:border-white/30 justify-start gap-2 bg-transparent"
          onClick={() => logout()}
          data-testid="button-logout"
        >
          <LogOut className="w-4 h-4" />
          Sair
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="flex items-center justify-between p-4 bg-slate-900 text-white lg:hidden">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-primary to-accent flex items-center justify-center">
            <span className="font-display font-bold text-white">R</span>
          </div>
          <span className="font-display font-bold text-lg">ReMonta</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Abrir menu"
                className="text-white hover:bg-white/10"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 border-r-0">
              <NavContent />
            </SheetContent>
          </Sheet>
        </div>
        <InstallApkDialog open={installDialogOpen} onOpenChange={setInstallDialogOpen} />
      </div>
    );
  }

  return (
    <div className="hidden lg:flex w-72 flex-col fixed inset-y-0 z-50">
      <NavContent />
      <InstallApkDialog open={installDialogOpen} onOpenChange={setInstallDialogOpen} />
    </div>
  );
}
